import React, { PropsWithChildren } from 'react'
import './platform-integration-modal.scss'
import { Modal, type ModalProps, type ButtonProps } from 'antd'
import classnames from 'classnames'

type ButtonType = ButtonProps['type'] | 'remove'
type AugmentedButtonType = Omit<ButtonProps, 'type'> & { type?: ButtonType }

type PlatformIntegrationModalProps = Omit<ModalProps, 'okButtonProps' | 'cancelButtonProps'> & {
    modalStyle?: 'default' | '2025'
    titleIcon?: React.ReactNode
    providerLogo?: React.ReactNode
    // override button props with augmented `type` prop
    okButtonProps?: AugmentedButtonType
    cancelButtonProps?: AugmentedButtonType
}

function getModalButtonProps(buttonType: 'ok' | 'cancel', props?: AugmentedButtonType): ButtonProps {
    const computedButtonType = !props?.type
        ? buttonType === 'cancel'
            ? 'text'
            : 'primary'
        : props.type === 'remove'
        ? 'text'
        : props.type

    return {
        ...props,
        className: classnames(`platform-integration-modal-action-${buttonType}`, props?.className, {
            [`action-type-${props?.type}`]: props?.type,
        }),
        type: computedButtonType,
    }
}

function NullRender() {
    return null
}

export const PlatformIntegrationModal: React.FC<PropsWithChildren<PlatformIntegrationModalProps>> = (props) => {
    const { modalStyle = 'default', titleIcon, providerLogo, children, ...modalProps } = props

    return (
        <Modal
            {...modalProps}
            className={classnames(
                'platform-integration-modal',
                modalProps.className,
                `modal-style-${modalStyle}`,
                'text--content--main',
            )}
            wrapClassName={classnames('platform-integration-modal-wrapper', modalProps.wrapClassName)}
            destroyOnClose={true}
            title={
                <div className="platform-integration-modal-header">
                    <div className="platform-integration-modal-header-left">
                        {titleIcon && <span className="platform-integration-modal-title-icon">{titleIcon}</span>}
                        <span className="platform-integration-modal-title">{modalProps.title}</span>
                    </div>
                    <div className="platform-integration-modal-header-right">
                        {providerLogo && <div className="platform-integration-modal-provider-logo">{providerLogo}</div>}
                    </div>
                </div>
            }
            okButtonProps={getModalButtonProps('ok', modalProps.okButtonProps)}
            cancelButtonProps={getModalButtonProps('cancel', modalProps.cancelButtonProps)}
            /** Antd Modal doesn't support null|false value for closeIcon until v5.7 */
            closeIcon={<NullRender />}
            maskClosable={false}
        >
            {children}
        </Modal>
    )
}
